import React from "react";
import { AppSelect } from "../AppSelect";

export type SettingsChoice<T> = {
  id: T;
  display: string;
};

// Geometry of a SettingsSegmentedControl: every option is SEGMENT_WIDTH wide,
// inside a track with p-1 padding and a 1px border. `SettingsSection` sets
// `--control-width` from this so other controls line up with a control of N
// options.
const SEGMENT_WIDTH = "6rem";
export const settingsControlWidth = (segments: number) =>
  `calc(${segments} * ${SEGMENT_WIDTH} + 0.5rem + 2px)`;

/**
 * Right-aligned slot (from `sm` up) as wide as the section's segmented
 * control (`controlSegments` on `SettingsSection`), so stacked controls in
 * one section share an edge.
 */
export const SettingsControlSlot = ({
  className = "",
  children,
}: {
  className?: string;
  children: React.ReactNode;
}) => <div className={`sm:ml-auto sm:w-[var(--control-width)] ${className}`}>{children}</div>;

export const SettingsSelect = <T extends string>(props: {
  label: string;
  choices: SettingsChoice<T>[];
  disabled?: boolean;
  onChange: (value: T) => void;
  value: T;
}) => (
  <SettingsControlSlot>
    <AppSelect {...props} />
  </SettingsControlSlot>
);

export const SettingsSegmentedControl = <T extends string | boolean>({
  label,
  choices,
  disabled = false,
  onChange,
  value,
}: {
  label: string;
  choices: SettingsChoice<T>[];
  disabled?: boolean;
  onChange: (value: T) => void;
  value: T;
}) => {
  const selectedIndex = Math.max(
    0,
    choices.findIndex(choice => choice.id === value),
  );
  return (
    <div className="flex sm:justify-end">
      <div
        role="group"
        aria-label={label}
        style={
          {
            "--segment-width": SEGMENT_WIDTH,
            "--segment-count": choices.length,
            "--segment-index": selectedIndex,
          } as React.CSSProperties
        }
        className="relative flex min-h-10 w-full flex-col rounded-full border border-outline bg-muted-surface p-1 sm:w-auto sm:flex-row"
      >
        {/* Sliding pill behind the selected option. Stacked vertically on
            mobile, side by side from `sm` up; every option has the same size,
            so the pill is 1/count of the track and translated by the index. */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute left-1 right-1 top-1 h-[calc((100%-0.5rem)/var(--segment-count))] translate-y-[calc(var(--segment-index)*100%)] rounded-full bg-surface shadow-sm ring-1 ring-outline transition-transform duration-200 ease-out motion-reduce:transition-none sm:bottom-1 sm:right-auto sm:h-auto sm:w-[calc((100%-0.5rem)/var(--segment-count))] sm:translate-x-[calc(var(--segment-index)*100%)] sm:translate-y-0"
        />
        {choices.map(choice => {
          const selected = value === choice.id;
          return (
            <button
              key={String(choice.id)}
              type="button"
              disabled={disabled}
              aria-pressed={selected}
              onClick={() => onChange(choice.id)}
              className={`relative z-10 min-w-0 break-words rounded-full px-2 py-1.5 text-center text-xs font-medium leading-tight transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:cursor-not-allowed disabled:opacity-60 sm:w-[var(--segment-width)] ${
                selected ? "text-text" : "text-text-mid hover:bg-surface/60 hover:text-text"
              }`}
            >
              {choice.display}
            </button>
          );
        })}
      </div>
    </div>
  );
};
